import React, { useEffect, useRef, useState } from "react"
import { Input } from "reactstrap"
import PayloadAutocomplete from "components/Common/PayloadAutocomplete"
import { useSamplePayloadPaths } from "utils/samplePayloadPaths"
import "./ExprBuilder.scss"

// ─── CONDITIONS mode ──────────────────────────────────────────────────────────

const OPERATORS = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is one of", value: "in" },
  { label: "is none of", value: "not_in" },
  { label: "starts with", value: "startswith" },
  { label: "ends with", value: "endswith" },
  { label: "contains", value: "contains" },
  { label: "is less than", value: "<" },
  { label: "is greater than", value: ">" },
  { label: "is at most", value: "<=" },
  { label: "is at least", value: ">=" },
  { label: "has any value", value: "truthy" },
  { label: "is empty", value: "isNone" },
  { label: "is set", value: "isNotNone" },
]

const NO_OPERAND_OPS = ["truthy", "isNone", "isNotNone"]

// A payload path: an identifier followed by one or more `.key` or `[…]`
// accessors. Matches stopRefs.PO, destLoc.city, stopIdsV2[stopId].orderId,
// stopRefs['PO']. A bare word (e.g. DROP_VAR) is NOT a path — it's a literal.
const PATH_RE = /^[a-zA-Z_$][\w$]*(\.[a-zA-Z_$][\w$]*|\[(?:\d+|'[^']*'|"[^"]*"|[a-zA-Z_$][\w$]*)\])+$/

function quoteResult(val) {
  if (!val || val.trim() === "") return "None"
  const t = val.trim()
  // Path like stopRefs.PO → stopRefs.get('PO'), stopIdsV2[stopId].orderId →
  // stopIdsV2[stopId].get('orderId')
  if (PATH_RE.test(t)) return buildPayloadGetSource(t)
  // Bare identifier that isn't a Python keyword → treat as string literal.
  // Symmetric with unquoteResult: parse strips 'DROP_VAR' → DROP_VAR for
  // clean display, so generate must put the quotes back.
  if (/^[a-zA-Z_]\w*$/.test(t) && !["None", "True", "False"].includes(t)) {
    return pyString(t)
  }
  return t
}

function parsePath(path) {
  const parts = []
  const pattern = /(?:^|\.)([A-Za-z_$][A-Za-z0-9_$]*)|\[['"]([^'"]+)['"]\]|\[(\d+)\]|\[([A-Za-z_$][A-Za-z0-9_$]*)\]/g
  let match

  while ((match = pattern.exec(path || "")) !== null) {
    if (match[1]) parts.push({ type: "key", value: match[1] })
    if (match[2]) parts.push({ type: "key", value: match[2] })
    if (match[3]) parts.push({ type: "index", value: match[3] })
    // Bracket subscript by variable, e.g. stopIdsV2[stopId] — kept as-is.
    if (match[4]) parts.push({ type: "var", value: match[4] })
  }

  return parts
}

function buildPayloadGetSource(path) {
  const parts = parsePath(path)
  if (parts.length === 0) return path

  let expr = parts[0].value

  for (const part of parts.slice(1)) {
    if (part.type === "index" || part.type === "var") {
      // numeric index or variable subscript, e.g. [0] / [stopId]
      expr = `${expr}[${part.value}]`
    } else {
      // string key → .get() so a missing key resolves to None, not KeyError
      expr = `${expr}.get(${pyString(part.value)})`
    }
  }

  return expr
}

function buildConditionVariable(cond) {
  const raw = cond.refKey || (cond.variable === "__custom__" ? cond.customVariable : cond.variable)
  let v = raw && PATH_RE.test(raw) ? buildPayloadGetSource(raw) : raw
  if (cond.caseModifier === "lower") v = `(${v} or '').lower()`
  if (cond.caseModifier === "upper") v = `(${v} or '').upper()`
  return v
}

function buildConditionStr({ variable, operator, operand }) {
  const v = variable
  switch (operator) {
    case "==":
    case "!=":
      return `${v} ${operator} '${operand}'`
    case "in":
      return `${v} in [${operand.split(",").map(x => `'${x.trim()}'`).join(", ")}]`
    case "not_in":
      return `${v} not in [${operand.split(",").map(x => `'${x.trim()}'`).join(", ")}]`
    case "startswith":
      return `${v}.startswith('${operand}')`
    case "endswith":
      return `${v}.endswith('${operand}')`
    case "contains":
      return `'${operand}' in ${v}`
    case "<":
    case ">":
    case "<=":
    case ">=":
      return `${v} ${operator} ${operand}`
    case "truthy":
      return v
    case "isNone":
      return `${v} is None`
    case "isNotNone":
      return `${v} is not None`
    default:
      return "True"
  }
}

const newCondition = (overrides = {}) => ({
  id: Date.now() + Math.random(),
  refKey: "",
  variable: "",
  customVariable: "",
  operator: "==",
  operand: "",
  caseModifier: "",
  ...overrides,
})

// ─── Expression parser (reverse of generate) ────────────────────────────────

// Reverse .get() chains back to dot paths: ext_attrbs.get('ordRevCode') → ext_attrbs.ordRevCode
function reverseGetChain(expr) {
  let result = expr.trim()
  // Replace .get('key') chains with .key
  while (/\.get\((['"])([^'"]+)\1\)/.test(result)) {
    result = result.replace(/\.get\((['"])([^'"]+)\1\)/, '.$2')
  }
  // Remove default args: .get('key', {}) already handled above
  return result
}

// Unquote a result value: 'foo' → foo, None → None
function unquoteResult(val) {
  const t = val.trim()
  if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
    return t.slice(1, -1)
  }
  return t
}

// Split "A if X else B if Y else C" into [{result, condition}, ...] + elseValue
// Right-associative: we scan left-to-right for top-level " if " / " else "
function splitBranches(expr) {
  const branches = []
  let remaining = expr.trim()

  while (true) {
    // Find top-level " if " (not inside parens/brackets)
    const ifIdx = findTopLevel(remaining, ' if ')
    if (ifIdx === -1) break

    const resultPart = remaining.slice(0, ifIdx).trim()
    const afterIf = remaining.slice(ifIdx + 4)

    // Find the matching top-level " else "
    const elseIdx = findTopLevel(afterIf, ' else ')
    if (elseIdx === -1) break

    const conditionPart = afterIf.slice(0, elseIdx).trim()
    remaining = afterIf.slice(elseIdx + 6).trim()

    branches.push({ resultValue: unquoteResult(resultPart), condition: conditionPart })
  }

  return { branches, elseValue: unquoteResult(remaining) }
}

// Find substring at top level (not inside parens, brackets, quotes)
function findTopLevel(str, needle) {
  let depth = 0
  let inSingle = false, inDouble = false
  for (let i = 0; i <= str.length - needle.length; i++) {
    const ch = str[i]
    if (ch === "'" && !inDouble) inSingle = !inSingle
    else if (ch === '"' && !inSingle) inDouble = !inDouble
    if (inSingle || inDouble) continue
    if ('([{'.includes(ch)) depth++
    else if (')]}'.includes(ch)) depth--
    if (depth === 0 && str.slice(i, i + needle.length) === needle) return i
  }
  return -1
}

// Split condition on top-level " or " into groups, then each group on " and "
function parseConditionGroups(condStr) {
  const orParts = splitTopLevelKeyword(condStr, ' or ')
  return orParts.map(part => {
    // Strip outer parens if wrapped
    let inner = part.trim()
    if (inner.startsWith('(') && inner.endsWith(')')) inner = inner.slice(1, -1).trim()
    const andParts = splitTopLevelKeyword(inner, ' and ')
    return andParts.map(parseOneCondition)
  })
}

function splitTopLevelKeyword(str, keyword) {
  const parts = []
  let depth = 0, inSingle = false, inDouble = false, last = 0
  for (let i = 0; i <= str.length - keyword.length; i++) {
    const ch = str[i]
    if (ch === "'" && !inDouble) inSingle = !inSingle
    else if (ch === '"' && !inSingle) inDouble = !inDouble
    if (inSingle || inDouble) continue
    if ('([{'.includes(ch)) depth++
    else if (')]}'.includes(ch)) depth--
    if (depth === 0 && str.slice(i, i + keyword.length) === keyword) {
      parts.push(str.slice(last, i).trim())
      last = i + keyword.length
    }
  }
  parts.push(str.slice(last).trim())
  return parts
}

function parseVarWithCase(raw) {
  const s = raw.trim()
  let m = s.match(/^\((.+?)\s+or\s+''\)\.(lower|upper)\(\)$/)
  if (m) return { refKey: reverseGetChain(m[1].trim()), caseModifier: m[2] }
  m = s.match(/^(.+?)\.(lower|upper)\(\)$/)
  if (m) return { refKey: reverseGetChain(m[1].trim()), caseModifier: m[2] }
  return { refKey: reverseGetChain(s), caseModifier: "" }
}

// Parse a single condition like "x == 'a'" or "x in ['a','b']" or "x.startswith('y')" etc.
function parseOneCondition(condStr) {
  const s = condStr.trim()

  // x is not None
  let m = s.match(/^(.+?)\s+is\s+not\s+None$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'isNotNone' })

  // x is None
  m = s.match(/^(.+?)\s+is\s+None$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'isNone' })

  // x not in [...]
  m = s.match(/^(.+?)\s+not\s+in\s+\[(.+)\]$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'not_in', operand: parseListOperand(m[2]) })

  // x in [...]
  m = s.match(/^(.+?)\s+in\s+\[(.+)\]$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'in', operand: parseListOperand(m[2]) })

  // 'val' in x (contains)
  m = s.match(/^['"](.+?)['"]\s+in\s+(.+)$/)
  if (m) return newCondition({ ...parseVarWithCase(m[2]), operator: 'contains', operand: m[1] })

  // x.startswith('y')
  m = s.match(/^(.+?)\.startswith\(['"](.+?)['"]\)$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'startswith', operand: m[2] })

  // x.endswith('y')
  m = s.match(/^(.+?)\.endswith\(['"](.+?)['"]\)$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: 'endswith', operand: m[2] })

  // x == 'y' or x != 'y'
  m = s.match(/^(.+?)\s*(==|!=)\s*['"](.+?)['"]$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: m[2], operand: m[3] })

  // x < y, x > y, x <= y, x >= y
  m = s.match(/^(.+?)\s*(<=|>=|<|>)\s*(.+)$/)
  if (m) return newCondition({ ...parseVarWithCase(m[1]), operator: m[2], operand: m[3].trim() })

  // Bare truthy: just a variable name
  if (/^[a-zA-Z_][\w.]*$/.test(s) || s.includes('.get(')) {
    return newCondition({ ...parseVarWithCase(s), operator: 'truthy' })
  }

  // Fallback — put the whole thing as refKey with truthy
  return newCondition({ refKey: s, operator: 'truthy' })
}

function parseListOperand(listInner) {
  return listInner.split(',').map(s => {
    const t = s.trim()
    if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) return t.slice(1, -1)
    return t
  }).join(',')
}

// Main entry: parse full expression into { branches, elseValue } or null if empty
export function parseExprToBranches(expr) {
  if (!expr || !expr.trim()) return null
  const trimmed = expr.trim()

  // No if/else at all — it's just a single value, not a conditional
  if (findTopLevel(trimmed, ' if ') === -1) return null

  const { branches: rawBranches, elseValue } = splitBranches(trimmed)
  if (rawBranches.length === 0) return null

  const branches = rawBranches.map(b => {
    const groups = parseConditionGroups(b.condition)
    return {
      id: Date.now() + Math.random(),
      resultValue: b.resultValue,
      conditionGroups: groups.map(conditions => ({
        id: Date.now() + Math.random(),
        all: conditions,
      })),
    }
  })

  return { branches, elseValue }
}

const newConditionGroup = () => ({
  id: Date.now() + Math.random(),
  all: [newCondition()],
})

const newBranch = () => ({
  id: Date.now() + Math.random(),
  resultValue: "",
  conditionGroups: [newConditionGroup()],
})

function buildConditionGroupsExpr(groups) {
  const conditionGroups = (groups || [])
    .map(group =>
      group.all
        .filter(cond => {
          const variable = buildConditionVariable(cond)
          return variable && cond.operator && (NO_OPERAND_OPS.includes(cond.operator) || cond.operand)
        })
        .map(cond =>
          buildConditionStr({
            ...cond,
            variable: buildConditionVariable(cond),
          })
        )
    )
    .filter(group => group.length > 0)

  if (conditionGroups.length === 0) return ""

  const expr = conditionGroups
    .map(group => {
      const joined = group.join(" and ")
      return group.length > 1 ? `(${joined})` : joined
    })
    .join(" or ")

  return conditionGroups.length > 1 ? `(${expr})` : expr
}

export function generateConditionExpr(branches, elseValue) {
  const hasAnyCondition = branches.some(
    b => buildConditionGroupsExpr(b.conditionGroups) !== ""
  )

  // No IF condition anywhere yet — return whatever the user typed in a THEN
  // or ELSE so a half-built expression doesn't silently collapse to "None".
  if (!hasAnyCondition) {
    const firstThen = branches.find(b => b.resultValue && b.resultValue.trim())
    if (firstThen) return quoteResult(firstThen.resultValue)
    return quoteResult(elseValue)
  }

  return branches.reduceRight((elseExpr, branch) => {
    const conditionExpr = buildConditionGroupsExpr(branch.conditionGroups)
    if (!conditionExpr) return elseExpr
    return `${quoteResult(branch.resultValue)} if ${conditionExpr} else ${elseExpr}`
  }, quoteResult(elseValue))
}

const ResultValueInput = ({ value, placeholder, options, onChange }) => (
  <div className="qed-expr-result">
    <PayloadAutocomplete
      options={options}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </div>
)

export const buildStateFrom = value => {
  const trimmed = (value || "").trim()
  if (!trimmed) {
    return { branches: [newBranch()], elseValue: "None", mode: "builder" }
  }
  const parsedExpr = parseExprToBranches(trimmed)
  if (parsedExpr) return { ...parsedExpr, mode: "builder" }
  // No `if` keyword → it's just a value with no conditions. Save round-trips:
  // the user saved zero branches, so reload should produce zero branches too.
  if (!/\bif\b/.test(trimmed)) {
    return { branches: [], elseValue: trimmed, mode: "builder" }
  }
  // Has `if` but isn't a shape we can parse — leave it in raw mode
  return { branches: [newBranch()], elseValue: "None", mode: "raw" }
}

const ConditionsMode = ({ onChange, initialValue }) => {
  const initial = React.useMemo(() => buildStateFrom(initialValue), [initialValue])
  const [branches, setBranches] = useState(() => initial.branches)
  const [elseValue, setElseValue] = useState(() => initial.elseValue)
  const [mode, setMode] = useState(() => initial.mode)
  const [rawValue, setRawValue] = useState(() => initialValue || "")
  const [parseWarning, setParseWarning] = useState(null)
  const payloadPaths = useSamplePayloadPaths()
  // EXPR evaluates as Python, so suggest the form the expression actually uses
  // (stopIdsV2[stopId].get('quedId')) rather than the dot form that won't resolve.
  const pathAutocompleteOptions = payloadPaths.map(item => ({
    value: buildPayloadGetSource(item.path),
    label: item.label,
  }))

  const updateBranchResult = (branchId, val) => {
    setBranches(prev =>
      prev.map(branch => (branch.id === branchId ? { ...branch, resultValue: val } : branch))
    )
  }

  const updateCondition = (branchId, groupId, conditionId, field, val) => {
    setBranches(prev =>
      prev.map(branch =>
        branch.id === branchId
          ? {
              ...branch,
              conditionGroups: branch.conditionGroups.map(group =>
                group.id === groupId
                  ? {
                      ...group,
                      all: group.all.map(cond =>
                        cond.id === conditionId ? { ...cond, [field]: val } : cond
                      ),
                    }
                  : group
              ),
            }
          : branch
      )
    )
  }

  const addCondition = (branchId, groupId) => {
    setBranches(prev =>
      prev.map(branch =>
        branch.id === branchId
          ? {
              ...branch,
              conditionGroups: branch.conditionGroups.map(group =>
                group.id === groupId ? { ...group, all: [...group.all, newCondition()] } : group
              ),
            }
          : branch
      )
    )
  }

  const removeCondition = (branchId, groupId, conditionId) => {
    setBranches(prev =>
      prev.map(branch => {
        if (branch.id !== branchId) return branch
        const totalConditions = branch.conditionGroups.reduce(
          (sum, g) => sum + g.all.length,
          0
        )
        if (totalConditions <= 1) return branch
        const nextGroups = branch.conditionGroups
          .map(group =>
            group.id === groupId
              ? { ...group, all: group.all.filter(cond => cond.id !== conditionId) }
              : group
          )
          .filter(group => group.all.length > 0)
        return { ...branch, conditionGroups: nextGroups }
      })
    )
  }

  const addOrGroup = branchId => {
    setBranches(prev =>
      prev.map(branch =>
        branch.id === branchId
          ? { ...branch, conditionGroups: [...branch.conditionGroups, newConditionGroup()] }
          : branch
      )
    )
  }

  const addBranch = () => setBranches(prev => [...prev, newBranch()])

  const removeBranch = branchId => {
    setBranches(prev => prev.filter(branch => branch.id !== branchId))
  }

  const preview = generateConditionExpr(branches, elseValue)
  const effectiveValue = mode === "raw" ? rawValue : preview
  const isInitialRender = useRef(true)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    onChange(effectiveValue)
  }, [effectiveValue])

  const switchToRaw = () => {
    setRawValue(preview)
    setParseWarning(null)
    setMode("raw")
  }

  const switchToBuilder = () => {
    const next = buildStateFrom(rawValue)
    setBranches(next.branches)
    setElseValue(next.elseValue)
    if (next.mode === "builder") {
      setParseWarning(null)
      setMode("builder")
    } else {
      setParseWarning(
        "Can't parse this expression back into the builder. Keep editing here, or clear it to start over."
      )
    }
  }

  return (
    <div className="qed-expr">
      <div className="qed-expr-mode-toggle">
        <button
          type="button"
          className={`qed-expr-mode-btn${mode === "builder" ? " is-active" : ""}`}
          onClick={() => mode === "raw" && switchToBuilder()}
        >
          Builder
        </button>
        <button
          type="button"
          className={`qed-expr-mode-btn${mode === "raw" ? " is-active" : ""}`}
          onClick={() => mode === "builder" && switchToRaw()}
        >
          Raw text
        </button>
      </div>

      {parseWarning && (
        <div className="qed-expr-warning">{parseWarning}</div>
      )}

      {mode === "raw" ? (
        <Input
          type="textarea"
          rows={5}
          bsSize="sm"
          className="qed-expr-raw-input"
          placeholder="e.g. 'PICKUP' if stopType == 'PICK' else 'DELIVERY'"
          value={rawValue}
          onChange={e => {
            setRawValue(e.target.value)
            setParseWarning(null)
          }}
        />
      ) : (
      <>
      {branches.map((branch, branchIndex) => (
        <div key={branch.id} className="qed-expr-branch-row">
          <div className="qed-expr-branch">
          {branch.conditionGroups.map((group, groupIndex) => (
            <React.Fragment key={group.id}>
              {group.all.map((cond, conditionIndex) => {
                const isHead = conditionIndex === 0
                const isElseIfHead = isHead && branchIndex > 0 && groupIndex === 0
                const isOrHead = isHead && groupIndex > 0
                const totalConditions = branch.conditionGroups.reduce(
                  (sum, g) => sum + g.all.length,
                  0
                )
                const keyword = branchIndex === 0 && groupIndex === 0 && conditionIndex === 0
                  ? "IF"
                  : isElseIfHead
                    ? "ELSE IF"
                    : isOrHead
                      ? "OR"
                      : "AND"
                return (
                <div key={cond.id} className="qed-expr-row">
                  <span className="qed-expr-keyword">{keyword}</span>
                  <div className="qed-expr-cell">
                    <PayloadAutocomplete
                      options={pathAutocompleteOptions}
                      value={cond.refKey || ""}
                      placeholder="Pick a value"
                      onChange={value => {
                        updateCondition(branch.id, group.id, cond.id, "refKey", value)
                        updateCondition(branch.id, group.id, cond.id, "operand", "")
                      }}
                    />
                  </div>
                  <div className="qed-expr-cell">
                    <Input bsSize="sm" type="select" value={cond.operator} onChange={e => {
                      updateCondition(branch.id, group.id, cond.id, "operator", e.target.value)
                      updateCondition(branch.id, group.id, cond.id, "operand", "")
                    }}>
                      {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                    </Input>
                  </div>
                  <div className="qed-expr-cell">
                    {!NO_OPERAND_OPS.includes(cond.operator) && (
                      <PayloadAutocomplete
                        options={pathAutocompleteOptions}
                        placeholder={cond.operator === "in" || cond.operator === "not_in" ? "Comma-separated" : "Value"}
                        value={cond.operand}
                        onChange={value => updateCondition(branch.id, group.id, cond.id, "operand", value)}
                      />
                    )}
                  </div>
                  <div className="qed-expr-row-actions">
                    {cond.caseModifier && (
                      <button
                        type="button"
                        className="qed-expr-case-badge"
                        title={`Case: ${cond.caseModifier}. Click to remove.`}
                        onClick={() => updateCondition(branch.id, group.id, cond.id, "caseModifier", "")}
                      >
                        {cond.caseModifier === "lower" ? "a" : "A"}
                      </button>
                    )}
                    {totalConditions > 1 && (
                      <button
                        type="button"
                        className="qed-circle-btn qed-circle-btn-danger"
                        title="Remove this condition"
                        onClick={() => removeCondition(branch.id, group.id, cond.id)}
                      >
                        <i className="fas fa-times-circle" />
                      </button>
                    )}
                  </div>
                </div>
                )
              })}
            </React.Fragment>
          ))}

            <div className="qed-expr-add-controls">
              <button
                type="button"
                className="qed-circle-btn"
                onClick={() => addCondition(branch.id, branch.conditionGroups[branch.conditionGroups.length - 1].id)}
              >
                <i className="fas fa-plus-circle" /> AND condition
              </button>
              <button
                type="button"
                className="qed-circle-btn"
                onClick={() => addOrGroup(branch.id)}
              >
                <i className="fas fa-plus-circle" /> OR alternative
              </button>
            </div>

            <div className="qed-expr-row qed-expr-row-result">
              <span className="qed-expr-keyword">THEN</span>
              <div className="qed-expr-cell-result">
                <ResultValueInput
                  options={pathAutocompleteOptions}
                  placeholder="path, expression, or literal"
                  value={branch.resultValue}
                  onChange={value => updateBranchResult(branch.id, value)}
                />
              </div>
            </div>

          </div>
          <i
            className="mdi mdi-trash-can font-size-16 text-danger pointer qed-expr-branch-trash"
            title={branchIndex === 0 ? "Remove this IF block" : "Remove this ELSE IF block"}
            onClick={() => removeBranch(branch.id)}
          />
        </div>
      ))}

      <div className="qed-expr-add-branch">
        <button type="button" className="qed-circle-btn" onClick={addBranch}>
          <i className="fas fa-plus-circle" /> {branches.length === 0 ? "if" : "else if"}
        </button>
      </div>

      <div className="qed-expr-row qed-expr-row-result">
        <span className="qed-expr-keyword">{branches.length === 0 ? "VALUE" : "ELSE"}</span>
        <div className="qed-expr-cell-result">
          <ResultValueInput
            options={pathAutocompleteOptions}
            placeholder="path, expression, or literal"
            value={elseValue}
            onChange={setElseValue}
          />
        </div>
      </div>

      <Preview value={preview} label="Preview" />
      </>
      )}
    </div>
  )
}

function pyString(value) {
  return `'${String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r")
    .replace(/\t/g, "\\t")
    .replace(/'/g, "\\'")}'`
}


// ─── Shared preview ───────────────────────────────────────────────────────────

const Preview = ({ value, label = "Preview:" }) => (
  <div className="qed-preview mt-3">
    <span className="qed-preview-label">{label}</span>
    <code>{value}</code>
  </div>
)

// ─── Root component ───────────────────────────────────────────────────────────

const ExprBuilder = ({ onChange, initialValue }) => {
  return (
    <div className="qed-expression-builder">
      <ConditionsMode onChange={onChange} initialValue={initialValue} />
    </div>
  )
}

export default ExprBuilder
